import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormFields } from '~/components/FormFields';
import { useTranslation } from 'react-i18next';
import Layout from '~/components/Layout';
import FaqService from '~/api/services/faq';
import notify from '~/components/Notify';
import { Icon, SemanticICONS, Tab } from 'semantic-ui-react';
import { useAuth } from '~/routes/components/AuthProvider';
import { handleErrors } from '~/utils/helpers';

const FaqBuilder: React.FunctionComponent = () => {
  const [isBusy, setIsBusy] = useState(false);
  const { fetchSystemData } = useAuth()
  const { handleSubmit, control, reset } = useForm({
    mode: 'onSubmit'
  });
  const [options, setOptions] = useState(null);
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<string | number>(0);

  const icons = ['question circle', 'question circle outline', 'question'];

  useEffect(() => {
    fetchFaqBuilderData();
  }, []);

  async function fetchFaqBuilderData() {
    setIsBusy(true);
    try {
      const responseOptions = await FaqService.getFaqBuilderOptions();
      setOptions(responseOptions.data);
      const responseEdit = await FaqService.getFaqDataSettings();
      responseOptions.data.languages.map((language) => {
        const index = responseEdit?.data?.texts[0].languages.findIndex(
          (languageText) => languageText.id === language.id
        );
        if (index < 0) {
          responseEdit?.data?.texts[0]?.languages.push({
            id: language.id,
            translation: ''
          });
        }
      });
      reset(responseEdit.data);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderFormContentArea(index, languageOptions) {
    const indexLanguage = options?.languages.findIndex(
      (language) => language.id === languageOptions.id
    );
    return (
      <>
        <FormFields.Input
          key={languageOptions.id}
          placeholder={t('Title')}
          control={control}
          width={8}
          style={{ marginLeft: 15 }}
          name={`texts[0].languages[${indexLanguage}].translation`}
        />
      </>
    );
  }

  function renderLanguageTab() {
    const tabs = options?.languages?.map((language, index) => {
      return {
        menuItem: options?.languages[index]?.label || null,
        render: () => renderFormContentArea(index, language)
      };
    });
    return tabs;
  }

  function renderForm() {
    return (
      <>
        <Tab
          menu={{ secondary: true, pointing: true }}
          activeIndex={activeIndex}
          renderActiveOnly={true}
          onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
          panes={renderLanguageTab()}
        />
        <div>
          <div style={{ margin: '40px 0px 40px 15px' }}>
            <FormFields.Checkbox
              control={control}
              defaultValue={0}
              checkboxValue={1}
              label={t('Activate FAQ?')}
              name={'status'}
            />
          </div>
          <div style={{ margin: '40px 0px 0px 15px' }}>
            <h1 style={{ fontWeight: 'bold' }}>{t('Positions')}</h1>
          </div>
          <div style={{ display: 'flex', columnGap: '10px', margin: '5px 0px 20px 15px' }}>
            {options?.positions.map((item, index) => {
              return (
                <FormFields.Checkbox
                  key={item?.key}
                  control={control}
                  checkboxValue={item?.key}
                  label={t(`${item.label}`)}
                  name={`positions[${index}]`}
                />
              );
            })}
          </div>
          <div style={{ margin: '0px 0px 0px 15px' }}>
            <h1 style={{ fontWeight: 'bold' }}>{t('Icon')}</h1>
          </div>
          <div style={{ display: 'flex', columnGap: '10px', margin: '5px 0px 0px 15px' }}>
            {icons.map((item) => {
              return (
                <div key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <FormFields.Checkbox
                    key={item}
                    control={control}
                    defaultValue={0}
                    id={item}
                    checkboxValue={item}
                    name={'icon'}
                  />
                  <Icon name={item as SemanticICONS}></Icon>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  async function onSubmit(data: any) {
    for (let i = 0; i < data?.positions.length; i++) {
      if (data?.positions[i] === null || data?.positions[i] === undefined || data?.positions[i] === 0) {
        data?.positions.splice(i, 1);
        i--;
      }
    }
    if (!data?.positions || data.positions?.length === 0) {
      notify(t('Please check one of the positions'), 'error');
      return
    }
    setIsBusy(true);
    try {
      await FaqService.updateFaqSettings(data);
      fetchSystemData()
      notify(t('Faq settings updated'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  return (
    <Layout
      isBusy={isBusy}
      title={t('FAQ Builder settings')}
      segmented
      onSubmit={handleSubmit(onSubmit)}
      action={t('Update')}
    >
      {renderForm()}
    </Layout>
  );
};

export default FaqBuilder;
